import { mapState } from 'vuex'
import { Component, Vue, Watch } from 'vue-property-decorator'
import 'reflect-metadata'
import '@/plugins'
import '@/bootstrap/global/filters'
import '@/bootstrap/global/loader-components'
import '@/bootstrap/validation/rules'
import '@/bootstrap/global/prototypes'
import '@/bootstrap/interceptors/response'
import '@/plugins/vuetify-google-autocomplete'
import GrtSecurityLayout from '@/modules/common/views/layouts/security/security.layout.vue'
import GtrSecurityRegisterLayout from '@/modules/common/views/layouts/security/security-register.layout.vue'
import GtrAdminLayout from '@/modules/common/views/layouts/level-one/admin/admin.layout.vue'
import GtrGlobalSolutionLayout from '@/modules/common/views/layouts/level-two/global-solution/global-solution.layout.vue'
import GtrGlobalSolutionLayoutWithOutSideBar from '@/modules/common/views/layouts/level-two/global-solution-without-sidebar/global-solution-without-sidebar.layout.vue'
import GtrEventLayout from '@/modules/common/views/layouts/level-two/event/event.layout.vue'
import GtrModuleLayout from '@/modules/common/views/layouts/level-two/module/module.layout.vue'
import GtrNotice from '@/modules/common/components/ui-core/gtr-notice-component/gtr-notice.vue'
import Container from 'typedi'
import ErrorHandlerService from '@/modules/common/services/error-handler.service'
import GtrStorage from '@/modules/common/services/storage.service'
import SecurityContext from '@/modules/common/services/security-context.service'
import GtrLearningLayout from '@/modules/common/views/layouts/level-two/learning/learning.layout.vue'
import RemoteSupportWidgetService from '@/modules/common/services/remote-support-widget.service'
import { resolveRemoteSupportWidgetState, resolveRemoteSupportWidgetUser } from '@/modules/common/utils/remote-support-widget-state.util'
import type { RemoteSupportWidgetState } from '@/modules/common/utils/remote-support-widget-state.util'

@Component({
  name: 'GtrApplication',
  computed: {
    ...mapState('common', ['loader', 'noticeDisplayed']),
    ...mapState('event', ['event']),
    ...mapState('security', ['currentUser'])
  },
  components: {
    'security-layout': GrtSecurityLayout,
    'security-register-layout': GtrSecurityRegisterLayout,
    'admin-layout': GtrAdminLayout,
    'global-solution-layout': GtrGlobalSolutionLayout,
    'global-solution-without-sidebar-layout': GtrGlobalSolutionLayoutWithOutSideBar,
    'event-layout': GtrEventLayout,
    'module-layout': GtrModuleLayout,
    'learning-layout': GtrLearningLayout,
    'gtr-notice': GtrNotice
  }
})
export default class GtrApplication extends Vue {
  event!: Record<string, any> | undefined;
  currentUser!: Record<string, any> | undefined;
  minutesUntilLogoutWarning = 15 // default to 15 minutes
  refreshTokenInterval = 600000 // 10 minutes

  data () {
    return {
      displayInactivityWarning: false,
      minutesUntilLogout: 0,
      themeClass: ''
    }
  }

  async created () {
    if (Container.get(SecurityContext).context()) {
      await this.refreshToken()
      this.applyTheme()
    }
    setInterval(this.checkForUserActivity, 60000) // Check every minute
    const stampUserActivity = (): void => {
      Container.get(GtrStorage).setItem('last_user_action', Date.now().toString())
    }
    document.addEventListener('mouseup', stampUserActivity)
    document.addEventListener('keydown', stampUserActivity)
    document.addEventListener('touchstart', stampUserActivity)
    document.addEventListener('scroll', this.throttle(stampUserActivity, 5000), { passive: true })
    this.$bus.$on('login', this.handleLogin)
    if (Container.get(GtrStorage).getItem('just_logged_in')) {
      this.handleLogin()
      Container.get(GtrStorage).removeItem('just_logged_in')
    }
  }

  throttle (fn: () => void, waitMs: number): () => void {
    let lastCall = 0
    return () => {
      const now = Date.now()
      if (now - lastCall >= waitMs) {
        lastCall = now
        fn()
      }
    }
  }

  get layout () {
    if (this.$route.meta && this.$route.meta.layout) {
      return `${this.$route.meta.layout}-layout`
    }
    return 'div'
  }

  get remoteSupportWidgetState (): RemoteSupportWidgetState {
    return resolveRemoteSupportWidgetState(this.$route.params, this.event)
  }

  @Watch('remoteSupportWidgetState', { immediate: true })
  @Watch('currentUser')
  onRemoteSupportWidgetStateChange () {
    const state = this.remoteSupportWidgetState
    const user = resolveRemoteSupportWidgetUser(this.currentUser)
    if ((state === 'general' || state === 'remote') && user) {
      RemoteSupportWidgetService.mount(state, user, this.$route.params.event_uuid)
    } else {
      RemoteSupportWidgetService.hide()
    }
  }

  checkForUserActivity () {
    if (Container.get(SecurityContext).context()) {
      const now = Date.now()
      const lastUserAction = Number(Container.get(GtrStorage).getItem('last_user_action'))
      const lastTokenRefresh = Number(Container.get(GtrStorage).getItem('last_token_refresh'))

      this.updateLocalUserSessionInfo()

      // Refresh token if all the following are true:
      // - the user is logged in
      // - the user was active on any tab or window during the last interval
      // - the token has not already been refreshed by any tab or window during the past interval
      if (now - lastUserAction <= this.refreshTokenInterval && now - lastTokenRefresh > this.refreshTokenInterval) {
        this.refreshToken()
      }
    }
  }

  async refreshToken () {
    try {
      const response = await this.$store.dispatch('security/refreshToken')
      if (response) {
        this.updateLocalUserSessionInfo()
        Container.get(GtrStorage).setItem('last_token_refresh', Date.now().toString())
      }
    } catch (err) {
      Container.get(ErrorHandlerService).error(err)
    }
  }

  updateLocalUserSessionInfo () {
    let sessionExpiresAt = Container.get(SecurityContext).expiresAt()
    if (sessionExpiresAt) {
      sessionExpiresAt = new Date(sessionExpiresAt).getTime()
      this.$data.minutesUntilLogout = Math.floor((sessionExpiresAt - Date.now()) / 60000)
      this.$data.displayInactivityWarning = this.$data.minutesUntilLogout <= this.minutesUntilLogoutWarning
    }
  }

  async handleLogin () {
    Container.get(GtrStorage).setItem('last_user_action', Date.now().toString())
    this.updateLocalUserSessionInfo()
    this.applyTheme()
  }

  applyTheme () {
    const isFamiliarUi = this.$hasPermission('ui.familiar_ui')
    this.$data.themeClass = isFamiliarUi ? 'theme-familiar-ui' : ''
    this.applyFamiliarUiFont(isFamiliarUi)
  }

  applyFamiliarUiFont (active: boolean) {
    const LINK_ID = 'familiar-ui-inter-font'
    const existing = document.getElementById(LINK_ID)
    if (active && !existing) {
      const link = document.createElement('link')
      link.id = LINK_ID
      link.rel = 'stylesheet'
      link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap'
      document.head.appendChild(link)
    } else if (!active && existing) {
      existing.remove()
    }
  }

  async stayLoggedIn () {
    this.$data.displayInactivityWarning = false
    this.refreshToken()
  }
}
